
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ui
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "src/@/components/ui/table";
import { Button } from 'src/@/components/ui/button';
import { Badge } from 'src/@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "src/@/components/ui/dialog";
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Separator } from 'src/@/components/ui/separator';

// icons
import Iconify from 'src/components/Iconify';

// actions
import v3_actionGetAgentFeedbacks from 'src/actions/v3/ai/v3_actionGetAgentFeedbacks';

export default function AiAgentFeedbackTab({ aiAgent, cliente }) {
    const { t } = useTranslation('settings');
    const { enqueueSnackbar } = useSnackbar();
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);

    // Filter & Pagination States
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    const fetchFeedbacks = async () => {
        if (!aiAgent?._id) return;

        setLoading(true);
        try {
            const response = await v3_actionGetAgentFeedbacks({
                agent_id: aiAgent._id,
                cliente_id: cliente?._id,
                page,
                limit,
                search: searchText, // Backend might need to support this
                sort: sortBy,       // Backend might need to support this
                order: sortOrder    // Backend might need to support this
            });

            if (response.status === 200) {
                setFeedbacks(response.data.data || []);
                // Assuming backend returns meta for pagination
                setTotalItems(response.data.pagination?.total_items || 0);
            }
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('aiAgents.feedback.toastLoadError', 'Erro ao carregar feedbacks'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFeedbacks();
    }, [aiAgent?._id, page, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const handleOpenDetails = (feedback) => {
        setSelectedFeedback(feedback);
        setOpenDetails(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'reportado': return 'destructive'; // red
            case 'resolvido': return 'default'; // green-ish usually or default primary
            case 'em_analise': return 'secondary'; // gray/yellow
            default: return 'outline';
        }
    };

    return (
        <div className="flex flex-col h-full w-full gap-6 p-2 md:p-0 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            {/* Header / Toolbar */}
            <div className="flex flex-col md:flex-row justify-between items-end md:items-center gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <div className="w-full md:w-1/3">
                    <div className="relative">
                        <Iconify icon="eva:search-fill" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" width={20} />
                        <input
                            type="text"
                            placeholder={t('aiAgents.feedback.searchPlaceholder', 'Buscar por usuário...')}
                            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && fetchFeedbacks()}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button
                        onClick={fetchFeedbacks}
                        disabled={loading}
                        variant="outline"
                        className="flex items-center gap-2 border-gray-200 text-gray-600 hover:text-violet-600 hover:bg-violet-50"
                    >
                        <Iconify icon="eva:refresh-fill" width={18} height={18} className={loading ? "animate-spin" : ""} />
                        <span>{t('aiAgents.feedback.refreshButton', 'Atualizar')}</span>
                    </Button>
                </div>
            </div>

            {/* Content / Table */}
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden flex flex-col">
                <Table>
                    <TableHeader className="bg-gray-50/50">
                        <TableRow className="hover:bg-transparent border-gray-100">
                            <TableHead className="w-[180px] pl-6 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('created_at')}>
                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {t('aiAgents.feedback.tableHeaderDate', 'Data')}
                                    {sortBy === 'created_at' && <Iconify icon={sortOrder === 'desc' ? "eva:arrow-down-fill" : "eva:arrow-up-fill"} />}
                                </div>
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('user_id')}>
                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {t('aiAgents.feedback.tableHeaderUser', 'Usuário')}
                                    {sortBy === 'user_id' && <Iconify icon={sortOrder === 'desc' ? "eva:arrow-down-fill" : "eva:arrow-up-fill"} />}
                                </div>
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('feedback_type')}>
                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {t('aiAgents.feedback.tableHeaderType', 'Tipo')}
                                    {sortBy === 'feedback_type' && <Iconify icon={sortOrder === 'desc' ? "eva:arrow-down-fill" : "eva:arrow-up-fill"} />}
                                </div>
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('priority')}>
                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {t('aiAgents.feedback.tableHeaderPriority', 'Prioridade')}
                                    {sortBy === 'priority' && <Iconify icon={sortOrder === 'desc' ? "eva:arrow-down-fill" : "eva:arrow-up-fill"} />}
                                </div>
                            </TableHead>
                            <TableHead className="cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('status')}>
                                <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    {t('aiAgents.feedback.tableHeaderStatus', 'Status')}
                                    {sortBy === 'status' && <Iconify icon={sortOrder === 'desc' ? "eva:arrow-down-fill" : "eva:arrow-up-fill"} />}
                                </div>
                            </TableHead>
                            <TableHead className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pr-6">{t('aiAgents.feedback.tableHeaderActions', 'Ações')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-24 text-center">
                                    <div className="flex justify-center items-center gap-2 text-gray-500">
                                        <Iconify icon="eos-icons:loading" className="animate-spin" width={24} />
                                        <span>{t('aiAgents.feedback.loadingText', 'Carregando feedbacks...')}</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : feedbacks.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} className="h-[400px]">
                                    <div className="flex flex-col items-center justify-center p-12 text-center h-full">
                                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-400">
                                            <Iconify icon="fluent:person-feedback-24-regular" width={32} />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('aiAgents.feedback.emptyTitle', 'Nenhum feedback encontrado')}</h3>
                                        <p className="text-gray-500 max-w-sm mb-6">
                                            {t('aiAgents.feedback.emptyDescription', 'Tente ajustar os filtros ou busque por outro termo.')}
                                        </p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            feedbacks.map((feedback) => (
                                <TableRow key={feedback._id} className="hover:bg-gray-50/50 border-gray-100 transition-colors cursor-pointer group" onClick={() => handleOpenDetails(feedback)}>
                                    <TableCell className="pl-6 font-medium text-gray-700">
                                        {feedback.created_at ? format(new Date(feedback.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR }) : '-'}
                                    </TableCell>
                                    <TableCell className="text-gray-600 font-medium">{feedback.userObj?.nome || t('aiAgents.feedback.unknownUserFallback', 'Usuário Desconhecido')}</TableCell>
                                    <TableCell className="capitalize text-gray-600">
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            {feedback.feedback_type?.replace('_', ' ') || t('aiAgents.feedback.typeFallbackOther', 'Outro')}
                                        </span>
                                    </TableCell>
                                    <TableCell className="capitalize">
                                        <Badge variant="outline" className={`font-normal ${feedback.priority === 'alta' ? 'border-red-200 bg-red-50 text-red-700' :
                                            feedback.priority === 'media' ? 'border-orange-200 bg-orange-50 text-orange-700' :
                                                'border-blue-200 bg-blue-50 text-blue-700'
                                            }`}>
                                            {feedback.priority}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={getStatusColor(feedback.status)} className="capitalize shadow-none font-medium">
                                            {feedback.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right pr-6">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-gray-400 group-hover:text-[#7C3AED] hover:bg-violet-50 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDetails(feedback);
                                            }}
                                        >
                                            <Iconify icon="solar:eye-bold-duotone" width={20} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
                    <span className="text-xs text-gray-500">
                        {t('aiAgents.feedback.paginationSummary', 'Mostrando {{count}} de {{total}} resultados', { count: feedbacks.length, total: totalItems })}
                    </span>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={page === 1 || loading}
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            className="h-8 w-8 p-0"
                        >
                            <Iconify icon="eva:arrow-ios-back-fill" />
                        </Button>
                        <span className="text-xs font-medium text-gray-700 min-w-[3rem] text-center">
                            {t('aiAgents.feedback.paginationPageLabel', 'Página {{page}}', { page })}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={feedbacks.length < limit || loading} // Simple check, ideally check against totalItems
                            onClick={() => setPage(p => p + 1)}
                            className="h-8 w-8 p-0"
                        >
                            <Iconify icon="eva:arrow-ios-forward-fill" />
                        </Button>
                    </div>
                </div>
            </div>

            <Dialog open={openDetails} onOpenChange={setOpenDetails}>
                <DialogContent className="max-w-[95vw] h-[95vh] p-0 gap-0 !rounded-2xl overflow-hidden shadow-2xl border-none bg-[#f0f2f5] flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white z-10 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className={`w-3 h-3 rounded-full ${selectedFeedback?.status === 'reportado' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                                selectedFeedback?.status === 'em_analise' ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]' :
                                    selectedFeedback?.status === 'resolvido' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-gray-400'
                                }`} />
                            <div>
                                <DialogTitle className="text-xl font-bold text-gray-800">{t('aiAgents.feedback.detailsTitle', 'Detalhes do Feedback')}</DialogTitle>
                                <DialogDescription className="text-sm text-gray-500 flex items-center gap-2">
                                    {t('aiAgents.feedback.idLabel', 'ID:')} <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-600 border border-gray-200">{selectedFeedback?._id}</span>
                                </DialogDescription>
                            </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => setOpenDetails(false)} className="rounded-full hover:bg-gray-100 text-gray-500">
                            <Iconify icon="mingcute:close-line" width={24} />
                        </Button>
                    </div>

                    <FeedbackDetails feedback={selectedFeedback} />
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FeedbackDetails({ feedback }) {
    const { t } = useTranslation('settings');

    if (!feedback) return null;

    // Filter messages for this chat only (Frontend fix for backend bug)
    const targetChatId = feedback.chatObj?.numeroCliente + '@c.us';

    // Sort messages
    const messages = (feedback.mensagens_relacionadas || [])
        .filter(m => m.chatId === targetChatId)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Helper for icons
    const getMediaTypeIcon = (type) => {
        switch (type) {
            case 'image': return 'eva:image-fill';
            case 'video': return 'eva:video-fill';
            case 'audio': case 'ptt': return 'eva:volume-up-fill';
            case 'document': return 'eva:file-fill';
            case 'sticker': return 'eva:smile-fill';
            default: return null;
        }
    };

    return (
        <div className="flex flex-1 overflow-hidden">
            {/* LEFT COLUMN - INFO */}
            <div className="w-1/2 flex flex-col bg-white border-r border-gray-200 h-full overflow-hidden">
                <ScrollArea className="flex-1 p-6">
                    <div className="space-y-6">

                        {/* Status Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                            <div>
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">{t('aiAgents.feedback.statusLabel', 'Status')}</p>
                                <Badge variant="outline" className={`capitalize ${feedback.status === 'reportado' ? 'border-red-200 bg-red-50 text-red-700' :
                                    feedback.status === 'resolvido' ? 'border-green-200 bg-green-50 text-green-700' :
                                        'border-amber-200 bg-amber-50 text-amber-700'
                                    }`}>
                                    {feedback.status}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">{t('aiAgents.feedback.priorityLabel', 'Prioridade')}</p>
                                <Badge variant="outline" className={`capitalize ${feedback.priority === 'alta' ? 'border-red-200 bg-red-50 text-red-700' :
                                    feedback.priority === 'media' ? 'border-orange-200 bg-orange-50 text-orange-700' :
                                        'border-blue-200 bg-blue-50 text-blue-700'
                                    }`}>
                                    {feedback.priority}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">{t('aiAgents.feedback.typeLabel', 'Tipo')}</p>
                                <Badge variant="secondary" className="capitalize bg-gray-200 text-gray-700 hover:bg-gray-300">
                                    {feedback.feedback_type?.replace('_', ' ') || t('aiAgents.feedback.typeFallbackOther', 'Outro')}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">{t('aiAgents.feedback.dateLabel', 'Data')}</p>
                                <p className="text-sm font-medium text-gray-700">
                                    {feedback.created_at && format(new Date(feedback.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                                </p>
                            </div>
                        </div>

                        <Separator className="bg-gray-100" />

                        {/* People Info */}
                        <div className="grid grid-cols-2 gap-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-bold text-lg">
                                    {feedback.userObj?.nome?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">{t('aiAgents.feedback.userLabel', 'Usuário')}</p>
                                    <p className="text-sm font-medium text-gray-900">{feedback.userObj?.nome || t('aiAgents.feedback.anonymousFallback', 'Anônimo')}</p>
                                    <p className="text-xs text-gray-500">{feedback.userObj?.email}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-lg">
                                    <Iconify icon="ri:robot-2-fill" width={20} />
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 font-semibold uppercase">{t('aiAgents.feedback.agentLabel', 'Agente')}</p>
                                    <p className="text-sm font-medium text-gray-900">{feedback.agentObj?.name || t('aiAgents.feedback.agentFallback', 'Agente IA')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Comparativo de Mensagens (Scope) */}
                        <div className="space-y-3">
                            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">{t('aiAgents.feedback.scopeSectionLabel', 'Escopo do Feedback')}</p>
                            <div className="grid grid-cols-1 gap-4">
                                {/* Errada */}
                                <div className="bg-red-50 border border-red-100 rounded-lg p-4 relative group hover:shadow-sm transition-shadow">
                                    <div className="absolute top-3 right-3 text-red-400">
                                        <Iconify icon="eva:close-circle-fill" width={20} />
                                    </div>
                                    <p className="text-xs font-bold text-red-700 uppercase mb-2">{t('aiAgents.feedback.wrongMessageLabel', 'Mensagem Incorreta')}</p>
                                    <p className="text-sm text-gray-800 leading-relaxed text-pretty">
                                        {feedback.scope?.msg_wrong || <span className="italic text-gray-400">{t('aiAgents.feedback.notInformedFallback', 'Não informado')}</span>}
                                    </p>
                                </div>

                                {/* Correta */}
                                <div className="bg-green-50 border border-green-100 rounded-lg p-4 relative group hover:shadow-sm transition-shadow">
                                    <div className="absolute top-3 right-3 text-green-400">
                                        <Iconify icon="eva:checkmark-circle-2-fill" width={20} />
                                    </div>
                                    <p className="text-xs font-bold text-green-700 uppercase mb-2">{t('aiAgents.feedback.correctSuggestionLabel', 'Sugestão Correta')}</p>
                                    <p className="text-sm text-gray-800 leading-relaxed text-pretty">
                                        {feedback.scope?.msg_correct || <span className="italic text-gray-400">{t('aiAgents.feedback.notInformedFallback', 'Não informado')}</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Prompt */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Prompt Snapshot</p>
                                <Badge variant="outline" className="text-[10px] h-5 font-normal">{t('aiAgents.feedback.promptSnapshotBadge', 'No momento do feedback')}</Badge>
                            </div>
                            <div className="bg-[#1e1e1e] text-gray-300 p-4 rounded-xl border border-gray-800 text-xs font-mono whitespace-pre-wrap max-h-[250px] overflow-y-auto shadow-inner custom-scrollbar">
                                {feedback.prompt_current || feedback.scope?.prompt_current || t('aiAgents.feedback.promptNotCapturedFallback', '// Prompt não capturado')}
                            </div>
                        </div>

                        {/* Notes */}
                        {feedback.notes && (
                            <div className="bg-yellow-50 border border-yellow-100 p-4 rounded-xl">
                                <p className="text-xs text-yellow-800 font-bold uppercase mb-1">{t('aiAgents.feedback.notesLabel', 'Observações')}</p>
                                <p className="text-sm text-yellow-900">{feedback.notes}</p>
                            </div>
                        )}

                    </div>
                </ScrollArea>
            </div>

            {/* RIGHT COLUMN - CHAT */}
            <div className="w-1/2 flex flex-col bg-[#e5ddd5] relative">
                {/* Chat Header */}
                <div className="h-16 bg-[#f0f2f5] border-b border-gray-300 flex items-center px-4 justify-between shrink-0 z-20 shadow-sm">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-500">
                            <Iconify icon="fluent:chat-20-filled" width={20} />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-800">{t('aiAgents.feedback.chatHistoryTitle', 'Histórico da Conversa')}</p>
                            <p className="text-xs text-gray-500">{t('aiAgents.feedback.chatHistorySubtitle', 'Visualização do contexto')}</p>
                        </div>
                    </div>
                </div>

                {/* Chat Background Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none z-0"
                    style={{ backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")' }}>
                </div>

                {/* Messages Area */}
                <ScrollArea className="flex-1 p-4 z-10 w-full">
                    <div className="space-y-2 pb-4">
                        {messages.length === 0 ? (
                            <div className="flex justify-center mt-10">
                                <div className="bg-white/90 shadow-sm rounded-lg px-4 py-2 text-xs text-gray-500">
                                    {t('aiAgents.feedback.noMessagesFound', 'Nenhuma mensagem encontrada neste contexto.')}
                                </div>
                            </div>
                        ) : (
                            messages.map((msg) => {
                                const isHighlighted = feedback.scope?.messages?.includes(msg.id) || false;
                                const isAgent = msg.fromMe;
                                const mediaIcon = getMediaTypeIcon(msg.type);

                                return (
                                    <div
                                        key={msg.id}
                                        id={`msg-${msg.id}`}
                                        className={`flex flex-col max-w-[80%] ${isAgent ? 'ms-auto items-end' : 'me-auto items-start'}`}
                                    >
                                        {/* Label */}
                                        <span className={`text-[10px] font-bold mb-1 px-1 ${isAgent ? 'text-green-800' : 'text-gray-600'}`}>
                                            {isAgent ? t('aiAgents.feedback.chatSenderAgent', 'Agente') : t('aiAgents.feedback.chatSenderUser', 'Usuário')}
                                        </span>

                                        <div
                                            className={`relative rounded-lg px-3 py-2 text-sm shadow-sm border transition-all duration-500
                                                ${isAgent
                                                    ? 'bg-[#d9fdd3] border-[#d9fdd3] rounded-tr-none text-gray-900'
                                                    : 'bg-white border-white rounded-tl-none text-gray-900'
                                                }
                                                ${isHighlighted ? 'ring-4 ring-amber-400 ring-opacity-70 shadow-lg scale-[1.02] z-10' : ''}
                                            `}
                                        >
                                            {/* Highlight Star */}
                                            {isHighlighted && (
                                                <div className="absolute -top-3 -right-3 bg-amber-500 text-white rounded-full p-1 shadow-sm border-2 border-white">
                                                    <Iconify icon="eva:star-fill" width={12} height={12} />
                                                </div>
                                            )}

                                            {/* Content */}
                                            <div className="flex flex-col">
                                                {/* Media Icon if any */}
                                                {mediaIcon && (
                                                    <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 mb-1 bg-black/5 p-1 rounded w-fit">
                                                        <Iconify icon={mediaIcon} />
                                                        <span className="capitalize">{msg.type}</span>
                                                    </div>
                                                )}

                                                {(msg.type === 'chat' || msg.type === 'text') && (
                                                    <p className="whitespace-pre-wrap leading-relaxed decoration-slice">
                                                        {msg.body || <span className="italic text-gray-400 text-xs">{t('aiAgents.feedback.noTextContentFallback', 'Sem conteúdo de texto')}</span>}
                                                    </p>
                                                )}

                                                {/* Metadata Footer */}
                                                <div className="flex items-center justify-end gap-1 mt-1 select-none">
                                                    <span className="text-[10px] text-gray-500/80">
                                                        {format(new Date(msg.timestamp * 1000), 'HH:mm')}
                                                    </span>
                                                    {isAgent && (
                                                        <Iconify icon="solar:check-read-linear" width={12} className="text-blue-500" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </ScrollArea>

                {/* Scroll to bottom anchor if needed, or stick to bottom */}
            </div>
        </div>
    );
}

